import { useEffect, useState, useMemo } from "react";
import { UseQueryResult } from "react-query";
import { Contact, contactsService } from "@/app/providers/services";
import { find } from "lodash";

import { JsonEditor } from "@/common";
import Button from "@imminently/decisively-ui/Button";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import { BackendResource } from "@imminently/immi-query";
import { Typography } from "@mui/material";
import { useNotify } from "@/common/notifications";
import { useFullRelease } from "@/common/hooks_useFullRelease";

type ContactResource = BackendResource<Contact & Record<string, any>>;

const useFullContact = (contact: Contact) => {
  return contactsService.useGetOne(contact.id) as UseQueryResult<ContactResource>;
};

// Check if a string is a GUID (UUID format)
const isGuid = (str: string): boolean => {
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return guidRegex.test(str);
};

// Recursively replace GUID keys with descriptions from the graph
const replaceGuidKeysWithDescriptions = (
  obj: any,
  graph: any
): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // If it's an array, process each element
  if (Array.isArray(obj)) {
    return obj.map(item => replaceGuidKeysWithDescriptions(item, graph));
  }

  // If it's not an object, return as-is
  if (typeof obj !== "object") {
    return obj;
  }

  // Process object
  const result: any = {};

  for (const [key, value] of Object.entries(obj)) {
    // Skip @id keys
    if (key === "@id") {
      result[key] = value;
      continue;
    }

    // If the value is an array, this key is an entity name - keep it as-is
    if (Array.isArray(value)) {
      result[key] = replaceGuidKeysWithDescriptions(value, graph);
      continue;
    }

    // If the key looks like a GUID, try to replace it with description
    if (isGuid(key)) {
      // Look up the node in the graph
      const node = find(graph?.nodes, { v: key });
      if (node?.value?.description) {
        // Replace GUID key with description
        result[node.value.description] = replaceGuidKeysWithDescriptions(value, graph);
      } else {
        // Node not found, keep GUID as-is
        result[key] = replaceGuidKeysWithDescriptions(value, graph);
      }
    } else {
      // Not a GUID key, keep as-is but process value recursively
      result[key] = replaceGuidKeysWithDescriptions(value, graph);
    }
  }

  return result;
};

export const ContactCodeView = ({ contact }: { contact: Contact; }) => {
  const { data: fullContact, isLoading, isError } = useFullContact(contact);
  const fullRelease = useFullRelease();
  const [jsonValue, setJsonValue] = useState<string>("");
  const [hasChanges, setHasChanges] = useState(false);
  const notify = useNotify();

  const { mutateAsync, isLoading: saving } = contactsService.useUpdate();

  // Build a map of description -> GUID for reverse lookup
  const descriptionToGuidMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (fullRelease?.rule_graph?.nodes) {
      fullRelease.rule_graph.nodes.forEach((node: any) => {
        if (node.v && node.value?.description) {
          map[node.value.description] = node.v;
        }
      });
    }
    return map;
  }, [fullRelease?.rule_graph]);

  // Process contact data to replace GUID keys with descriptions
  const processedContact = useMemo(() => {
    if (!fullContact || !fullRelease?.rule_graph) {
      return fullContact;
    }

    // Only process the custom field
    if (!fullContact.custom) {
      return fullContact;
    }

    return {
      ...fullContact,
      custom: replaceGuidKeysWithDescriptions(fullContact.custom, fullRelease.rule_graph),
    };
  }, [fullContact, fullRelease?.rule_graph]);

  // Reverse function: replace descriptions with GUIDs
  const replaceDescriptionsWithGuids = (obj: any, descriptionMap: Record<string, string>): any => {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => replaceDescriptionsWithGuids(item, descriptionMap));
    }

    if (typeof obj !== "object") {
      return obj;
    }

    const result: any = {};

    for (const [key, value] of Object.entries(obj)) {
      if (key === "@id") {
        result[key] = value;
        continue;
      }

      if (Array.isArray(value)) {
        result[key] = replaceDescriptionsWithGuids(value, descriptionMap);
        continue;
      }

      // If the key is a description (exists in our map), convert back to GUID
      if (descriptionMap[key]) {
        result[descriptionMap[key]] = replaceDescriptionsWithGuids(value, descriptionMap);
      } else {
        // Check if it's already a GUID or some other key
        if (isGuid(key)) {
          result[key] = replaceDescriptionsWithGuids(value, descriptionMap);
        } else {
          // It's a description that doesn't exist in map, or an entity name - keep as-is
          result[key] = replaceDescriptionsWithGuids(value, descriptionMap);
        }
      }
    }

    return result;
  };

  const handleChange = (value: string) => {
    setJsonValue(value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      const parsed = JSON.parse(jsonValue);

      // Convert descriptions back to GUIDs in the custom field
      if (parsed.custom && fullRelease?.rule_graph) {
        parsed.custom = replaceDescriptionsWithGuids(parsed.custom, descriptionToGuidMap);
      }

      await mutateAsync({
        id: contact.id,
        payload: parsed,
      });
      notify.success("Contact updated successfully");
      contactsService.invalidateAllQueries();
      setHasChanges(false);
    } catch (error) {
      if (error instanceof SyntaxError) {
        notify.error("Invalid JSON format");
      } else {
        notify.error(`Error updating contact: ${(error as any)?.message || "Unknown error"}`);
      }
    }
  };

  const handleReset = () => {
    if (processedContact) {
      setJsonValue(JSON.stringify(processedContact, null, 2));
      setHasChanges(false);
    }
  };

  // Keep the editor in sync while data is loading, but never overwrite user edits.
  useEffect(() => {
    if (processedContact && (!jsonValue || !hasChanges)) {
      setJsonValue(JSON.stringify(processedContact, null, 2));
    }
  }, [processedContact, jsonValue, hasChanges]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  if (isError) {
    return <Typography>Error loading contact</Typography>;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center gap-2 p-4 border-b">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleReset}
          disabled={!hasChanges}
          style={{ marginLeft: "auto" }}
         label="Reset">
          Reset
        </Button>
        <Button label={saving ? "Saving..." : "Save"}
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        />
      </div>
      <div className="flex-1 overflow-hidden flex flex-col">
        <JsonEditor
          value={jsonValue}
          onChange={handleChange}
        />
      </div>
    </div>
  );
};
